import { useCallback, useEffect } from 'react';

interface UsePiPHandlersProps {
  audioRef: React.RefObject<HTMLAudioElement | null>;
  isMuted: boolean;
  isWebRtcConnected: boolean;
  muteMicrophone: (muted: boolean) => void;
  stopWebrtc: () => void;
  stopScreenShare: () => void;
  handleDisconnect: (preventNavigation?: boolean) => void;
  onMicStateChange?: (isMuted: boolean) => void;
}

interface UsePiPHandlersReturn {
  handlePiPVolumeChange: (volume: number) => void;
  handlePiPMuteChange: (muted: boolean) => void;
  handlePiPMicMuteChange: (micMuted: boolean) => void;
  stopSessionFromPiP: () => void;
}

/**
 * Custom hook to manage Picture-in-Picture event handlers and state synchronization
 * Handles audio volume/mute changes, microphone muting, and session termination from PiP window
 */
export const usePiPHandlers = ({
  audioRef,
  isMuted,
  isWebRtcConnected,
  muteMicrophone,
  stopWebrtc,
  stopScreenShare,
  handleDisconnect,
  onMicStateChange,
}: UsePiPHandlersProps): UsePiPHandlersReturn => {
  // Handle volume changes from PiP window
  const handlePiPVolumeChange = useCallback(
    (volume: number) => {
      // Dispatch custom event to notify AudioControl components
      window.dispatchEvent(
        new CustomEvent('audioControlSync', {
          detail: { volume, muted: audioRef.current?.muted ?? false },
        }),
      );
    },
    [audioRef],
  );

  // Handle mute changes from PiP window
  const handlePiPMuteChange = useCallback(
    (muted: boolean) => {
      // Dispatch custom event to notify AudioControl components
      window.dispatchEvent(
        new CustomEvent('audioControlSync', {
          detail: { volume: audioRef.current?.volume ?? 1, muted },
        }),
      );
    },
    [audioRef],
  );

  // Handle microphone mute changes from PiP window
  const handlePiPMicMuteChange = useCallback(
    (micMuted: boolean) => {
      // Apply microphone mute state directly
      muteMicrophone(micMuted);
    },
    [muteMicrophone],
  );

  // Stop session from PiP window
  const stopSessionFromPiP = useCallback(() => {
    handleDisconnect(false);

    if (isWebRtcConnected) {
      stopScreenShare();
      stopWebrtc();
    }
  }, [isWebRtcConnected, stopWebrtc, stopScreenShare, handleDisconnect]);

  // Dispatch mic state changes for listeners (including PiP window)
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('micStateChange', {
        detail: { isMuted },
      }),
    );
    onMicStateChange?.(isMuted);
  }, [isMuted, onMicStateChange]);

  // Listen for mic control sync events from PiP window (to mute/unmute mic)
  useEffect(() => {
    const handleMicControlSync = (event: Event) => {
      const customEvent = event as CustomEvent<{ muted: boolean }>;
      const { muted } = customEvent.detail;
      if (muteMicrophone) {
        muteMicrophone(muted);
      }
    };

    window.addEventListener('micControlSync', handleMicControlSync);
    return () => {
      window.removeEventListener('micControlSync', handleMicControlSync);
    };
  }, [muteMicrophone]);

  return {
    handlePiPVolumeChange,
    handlePiPMuteChange,
    handlePiPMicMuteChange,
    stopSessionFromPiP,
  };
};
